import React from "react";
import { Box, Typography } from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useTranslation } from "react-i18next";
import {
  SUPPORT_EMAIL,
  SUPPORT_PHONES,
  phonesForRegion,
  SupportPhoneEntry,
} from "../constants/supportContacts";
import { ltrIsolateSx } from "../helper/documentDirection";
import { NJIM } from "../theme/njimTheme";

const TEXT_SECONDARY = "#71809B";
const BRAND_INK = "#162D3A";

type SupportContactLinksProps = {
  variant: "login" | "popover";
};

const phoneLinkSx = {
  login: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    fontSize: 12,
    fontWeight: 500,
    color: TEXT_SECONDARY,
    textDecoration: "none",
    "&:hover": { color: BRAND_INK },
  },
  popover: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    p: 1.5,
    borderRadius: "12px",
    bgcolor: NJIM.muted,
    textDecoration: "none",
    transition: "background 0.2s ease",
    "&:hover": { bgcolor: NJIM.mintWash },
  },
} as const;

const PopoverPhoneRow: React.FC<{ phone: SupportPhoneEntry; regionLabel: string }> = ({
  phone,
  regionLabel,
}) => (
  <Box
    component="a"
    href={`tel:${phone.tel}`}
    sx={{ ...phoneLinkSx.popover, mb: 1.25 }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        bgcolor: "#fff",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      <PhoneOutlinedIcon sx={{ fontSize: 18, color: NJIM.ink }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
        {regionLabel}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, ...ltrIsolateSx }}>
        {phone.display}
      </Typography>
    </Box>
  </Box>
);

const LoginRegionGroup: React.FC<{
  regionKey: "iraq" | "kurdistan";
  phones: SupportPhoneEntry[];
}> = ({ regionKey, phones }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "rgba(113,128,155,0.85)",
          mb: 0.5,
        }}
      >
        {t(`support.region${regionKey === "iraq" ? "Iraq" : "Kurdistan"}`)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        {phones.map((phone, index) => (
          <React.Fragment key={phone.id}>
            {index > 0 && (
              <Typography sx={{ color: "#E6EAF1", fontSize: 12, userSelect: "none" }}>
                ·
              </Typography>
            )}
            <Box component="a" href={`tel:${phone.tel}`} sx={{ ...phoneLinkSx.login, ...ltrIsolateSx }}>
              <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
              {phone.display}
            </Box>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const SupportContactLinks: React.FC<SupportContactLinksProps> = ({ variant }) => {
  const { t } = useTranslation();

  if (variant === "login") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
          width: "100%",
        }}
      >
        <LoginRegionGroup regionKey="iraq" phones={phonesForRegion("iraq")} />
        <LoginRegionGroup regionKey="kurdistan" phones={phonesForRegion("kurdistan")} />
        <Box
          component="a"
          href={`mailto:${SUPPORT_EMAIL}`}
          sx={{ ...phoneLinkSx.login, ...ltrIsolateSx }}
        >
          <EmailOutlinedIcon sx={{ fontSize: 14 }} />
          {SUPPORT_EMAIL}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {SUPPORT_PHONES.map((phone) => (
        <PopoverPhoneRow
          key={phone.id}
          phone={phone}
          regionLabel={t(
            `support.region${phone.region === "iraq" ? "Iraq" : "Kurdistan"}`
          )}
        />
      ))}

      <Box
        component="a"
        href={`mailto:${SUPPORT_EMAIL}`}
        sx={phoneLinkSx.popover}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: "#fff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <EmailOutlinedIcon sx={{ fontSize: 18, color: NJIM.ink }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
            {t("header.supportEmailLabel")}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: NJIM.textPrimary,
              wordBreak: "break-all",
              ...ltrIsolateSx,
            }}
          >
            {SUPPORT_EMAIL}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SupportContactLinks;
